import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Skeleton'
import { Toggle } from '@/components/ui/Toggle'
import { useSystemSettings, useUpdateSystemSettings } from '@/lib/settings-api'
import type { SystemSetting } from '@/lib/settings-types'

export function SystemTab() {
  const settingsQuery = useSystemSettings(true)
  const update = useUpdateSystemSettings()
  const settings = settingsQuery.data ?? []
  const clientReports = settings.filter((s) => s.group === 'client_reports')
  const invoices = settings.filter((s) => s.group === 'invoices')
  const graph = settings.filter((s) => s.group === 'graph')

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Mail naar klanten"
          subtitle="Het enige dat vanuit dit systeem automatisch naar buiten gaat. Meldingen aan collega's stelt iedereen zelf in onder Meldingen."
        />
        {settingsQuery.isError ? (
          <ErrorState compact error={settingsQuery.error} onRetry={() => settingsQuery.refetch()} />
        ) : settingsQuery.isLoading ? (
          <Skeleton className="mt-4 h-16 w-full" />
        ) : (
          <div className="mt-4 divide-y divide-hairline">
            {clientReports.map((setting) => (
              <div key={setting.key} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{setting.label}</p>
                  <p className="mt-0.5 text-sm text-ink-faint">{setting.help}</p>
                </div>
                <Toggle
                  checked={setting.value === true}
                  onChange={(value) => update.mutate({ [setting.key]: value })}
                  label={setting.label}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Facturen"
          subtitle="Wanneer een openstaande factuur jouw aandacht vraagt. Hier gaat niets van naar de klant; een betalingsherinnering blijft een handmatige actie."
        />
        {settingsQuery.isError ? (
          <ErrorState compact error={settingsQuery.error} onRetry={() => settingsQuery.refetch()} />
        ) : settingsQuery.isLoading ? (
          <Skeleton className="mt-4 h-16 w-full" />
        ) : (
          <div className="mt-4 divide-y divide-hairline">
            {invoices.map((setting) => (
              <div key={setting.key} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{setting.label}</p>
                  <p className="mt-0.5 text-sm text-ink-faint">{setting.help}</p>
                </div>
                <DaysSettingEditor
                  setting={setting}
                  onSave={(value) => update.mutate({ [setting.key]: value })}
                  pending={update.isPending}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Koppelingen: Microsoft Graph (mail)" subtitle="Gevoelige waarden zijn alleen schrijfbaar en worden versleuteld opgeslagen" />
        {settingsQuery.isLoading ? (
          <Skeleton className="mt-4 h-32 w-full" />
        ) : (
          <div className="mt-4 space-y-5">
            {graph.map((setting) => (
              <ConnectionSettingEditor key={setting.key} setting={setting} onSave={(value) => update.mutate({ [setting.key]: value })} pending={update.isPending} />
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Environment" subtitle="Bewust niet in de app instelbaar" />
        <p className="mt-4 text-sm text-ink-faint">
          Database, APP_SECRET, login (Entra OIDC), authenticatiemodus, mail-transport en service-URL's blijven environment-variabelen:
          die zijn nodig voordat de app draait of vormen een veiligheidslaag die niet vanuit de app zelf omgezet mag kunnen worden.
          De Graph-mailkoppeling hierboven valt terug op de environment zolang hier niets is ingesteld.
          Zie <span className="font-medium text-ink-soft">.env.example</span> en <span className="font-medium text-ink-soft">docs/80-deployment.md</span>.
        </p>
      </Card>
    </div>
  )
}

/**
 * Een aantal dagen. Bewust met een Opslaan-knop in plaats van opslaan per
 * toetsaanslag: tijdens het typen van "40" is "4" een geldige tussenstand die je
 * niet wilt wegschrijven.
 */
function DaysSettingEditor({ setting, onSave, pending }: { setting: SystemSetting; onSave: (value: number) => void; pending: boolean }) {
  const stored = typeof setting.value === 'number' ? setting.value : 0
  const [value, setValue] = useState(String(stored))
  const parsed = Number(value)
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 365
  const dirty = value !== String(stored)

  return (
    <div className="flex shrink-0 items-center gap-2">
      <input
        type="number"
        min={1}
        max={365}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label={setting.label}
        className="field num h-9 w-20 text-right"
      />
      <span className="text-sm text-ink-faint">dagen</span>
      <button type="button" className="btn-secondary h-9" onClick={() => onSave(parsed)} disabled={!dirty || !valid || pending}>
        Opslaan
      </button>
    </div>
  )
}

function ConnectionSettingEditor({ setting, onSave, pending }: { setting: SystemSetting; onSave: (value: string | null) => void; pending: boolean }) {
  const isSecret = setting.type === 'secret'
  const [value, setValue] = useState(isSecret ? '' : String(setting.value ?? ''))
  const overridden = setting.source === 'db'
  const dirty = isSecret ? value.trim() !== '' : value !== String(setting.value ?? '')

  return (
    <div>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-ink">{setting.label}</p>
        <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-faint">
          {overridden ? 'uit app-instellingen' : 'uit environment'}
        </span>
        {isSecret && setting.isSet ? <span className="rounded-pill bg-accent-600/12 px-2 py-0.5 text-xs text-accent-700">ingesteld</span> : null}
      </div>
      <p className="mt-0.5 text-sm text-ink-faint">{setting.help}</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          type={isSecret ? 'password' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="field max-w-md font-mono text-[13px]"
          placeholder={isSecret ? (setting.isSet ? '•••••••• (vervang door nieuwe waarde)' : 'Nog niet ingesteld') : ''}
          autoComplete="off"
        />
        <button type="button" className="btn-primary" onClick={() => onSave(value.trim())} disabled={!dirty || !value.trim() || pending}>
          Opslaan
        </button>
        {overridden ? (
          <button type="button" className="btn-ghost" onClick={() => { setValue(''); onSave(null) }} disabled={pending}>
            Gebruik environment
          </button>
        ) : null}
      </div>
    </div>
  )
}
